import { Box, Link, Typography } from '@mui/material';
import { mapDispatchToProps, mapStateToProps, mergeProps } from './SignInConfirm.container';

import AuthenticatedRoutes from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import { FormikTextField } from '/b2b/common/components/FormControls/TextField';
import FormikValidationForm from '/b2b/common/components/organisms/FormikValidationForm';
import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import React from 'react';
import ReselectMFA from '../../SelectMFA/components/ReselectMFA';
import { Link as RouterLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { signInConfirmSchema } from './SignInConfirm.schema';

const MFA_TEXT = {
    TOTP: {
        title: 'auth.signIn.confirm.title',
        subtitle: 'auth.signIn.confirm.mfa.totp.description',
        placeholder: 'auth.amplify.mfa.totp.placeholder',
        submitText: 'auth.continueSignIn',
    },
    SMS: {
        title: 'auth.signIn.confirm.title',
        subtitle: 'auth.signIn.confirm.mfa.sms.description',
        placeholder: 'auth.amplify.mfa.sms.placeholder',
        submitText: 'auth.continueSignIn',
    },
};

// eslint-disable-next-line no-use-before-define
export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(SignInConfirm);

export function SignInConfirm(props) {
    const { codeDeliveryDetails, confirmSignIn, mfaType, resendCode, signOut, schema } = props;

    const title = MFA_TEXT[mfaType]?.title;
    const subtitle = MFA_TEXT[mfaType]?.subtitle;
    const submitText = MFA_TEXT[mfaType]?.submitText ?? 'auth.continueSignIn';

    const autoFillProps = {
        autoComplete: mfaType === 'TOTP' ? 'one-time-code' : 'off',
        inputProps: {
            // Prevent 1Password auto-fill
            ['data-1p-ignore']: mfaType === 'TOTP' ? undefined : true,
            ['data-op-ignore']: mfaType === 'TOTP' ? undefined : true,
        },
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            alignItems="stretch"
            justifyContent="center"
            sx={{ mx: 'auto', px: 2, maxWidth: 700, width: '100%' }}
        >
            <FormikValidationForm
                data-test-id="sign-in-confirm__validation-form"
                initialValues={{ mfaType }}
                validationSchema={schema}
                onSubmit={confirmSignIn}
                title={title}
                subtitle={subtitle}
                submitText={submitText}
                enableReinitialize
                translationOptions={codeDeliveryDetails ?? {}}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    flexGrow={1}
                    gap={0.5}
                    alignItems="center"
                    mt={4}
                    mb={1}
                    sx={theme => (theme.breakpoints.down('lg') ? { justifyContent: 'center' } : {})}
                >
                    <FormikTextField
                        name="code"
                        label="common.fields.code.label"
                        placeholder={MFA_TEXT[mfaType]?.placeholder}
                        fullWidth
                        {...autoFillProps}
                    />
                </Box>
            </FormikValidationForm>
            <Box mt={5}>
                {['SMS', 'EMAIL'].includes(mfaType) && (
                    <Typography variant="body1" mb={2}>
                        <Link
                            fontWeight="bold"
                            data-test-id="sign-in-confirm__resend-code-link"
                            onClick={resendCode}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Locale path="password.resendCode" />
                        </Link>
                    </Typography>
                )}
                <ReselectMFA mfaType={mfaType} />
                <Typography variant="body1">
                    <Locale path="auth.shouldUseDifferentAccount" />{' '}
                    <Link
                        data-test-id="sign-in-confirm__sign-out-link"
                        fontWeight="bold"
                        onClick={signOut}
                        component={RouterLink}
                        to={AuthenticatedRoutes.LOGOUT}
                        sx={{ cursor: 'pointer' }}
                    >
                        <Locale path="auth.useDifferentAccount" />
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
}

SignInConfirm.propTypes = {
    codeDeliveryDetails: PropTypes.object,
    confirmSignIn: PropTypes.func.isRequired,
    mfaType: PropTypes.string,
    resendCode: PropTypes.func.isRequired,
    schema: PropTypes.object,
    signOut: PropTypes.func.isRequired,
};

SignInConfirm.defaultProps = {
    schema: signInConfirmSchema,
};
